import { TreeSelect } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useBusinessUnitStore } from '../../../contexts/mobx';
import {
    SELECT_ALL_VALUE,
    useConstructTreeDataByCode,
} from './hooks/use-construct-tree-data-by-code';
import sharedStyle from '../shared/style.module.scss';
import style from './style.module.scss';

interface ProvinceCodesSelectTreeProps {
    value?: string[];
    onChange?: (value: string[]) => void;
    disabled?: boolean;
    /** Shows a single "All" chip when every province is selected, instead
     * of "Select all" followed by the first few regions. */
    summarizeAll?: boolean;
    /** Shows the dropdown caret, which antd hides on multi-selects. */
    showArrow?: boolean;
    /** Replaces antd's caret, e.g. with one from the CMS's own icon set. */
    suffixIcon?: React.ReactNode;
}

const MAX_VISIBLE_TAGS = 3;

type TreeNode = {
    value: string | typeof SELECT_ALL_VALUE;
    children?: TreeNode[];
};

const getProvinceCodeFromValue = (value: string | number) =>
    typeof value === 'string' && value.startsWith('province-')
        ? value.replace('province-', '')
        : undefined;

const getSelectedTagProvinceCounts = (
    treeData: TreeNode[],
    selectedProvinceCodes: Set<string>
) =>
    treeData.flatMap((node) => {
        if (node.value === SELECT_ALL_VALUE) {
            return [];
        }

        const childProvinceCodes =
            node.children
                ?.map((child) => getProvinceCodeFromValue(child.value))
                .filter((provinceCode): provinceCode is string =>
                    Boolean(provinceCode)
                ) ?? [];

        if (childProvinceCodes.length === 0) {
            const provinceCode = getProvinceCodeFromValue(node.value);
            return provinceCode && selectedProvinceCodes.has(provinceCode)
                ? [1]
                : [];
        }

        const selectedChildCount = childProvinceCodes.filter((provinceCode) =>
            selectedProvinceCodes.has(provinceCode)
        ).length;

        if (selectedChildCount === 0) {
            return [];
        }

        return selectedChildCount === childProvinceCodes.length
            ? [selectedChildCount]
            : Array(selectedChildCount).fill(1);
    });

export const ProvinceCodesSelectTree = observer(
    ({
        value = [],
        onChange,
        disabled = false,
        summarizeAll = false,
        showArrow = false,
        suffixIcon,
    }: ProvinceCodesSelectTreeProps) => {
        const businessUnitStore = useBusinessUnitStore();

        useEffect(() => {
            businessUnitStore.fetchBusinessUnits();
        }, [businessUnitStore]);

        const {
            treeData,
            allProvinceCodes,
            businessUnitToProvincesMap,
        } = useConstructTreeDataByCode({
            businessUnitStore,
        });

        const selectedProvinceCodes = React.useMemo(
            () => value.filter((code) => allProvinceCodes.includes(code)),
            [allProvinceCodes, value]
        );

        const treeSelectValue = React.useMemo(() => {
            if (allProvinceCodes.length === 0) return [];
            if (selectedProvinceCodes.length === 0) return [];
            const hasSelectAll =
                selectedProvinceCodes.length === allProvinceCodes.length;
            if (hasSelectAll) {
                return [
                    SELECT_ALL_VALUE,
                    ...allProvinceCodes.map((code) => `province-${code}`),
                ];
            }
            return selectedProvinceCodes.map((code) => `province-${code}`);
        }, [allProvinceCodes, selectedProvinceCodes]);

        const hiddenProvinceCount = React.useMemo(() => {
            if (selectedProvinceCodes.length === 0) {
                return 0;
            }

            const visibleProvinceCount = getSelectedTagProvinceCounts(
                treeData as TreeNode[],
                new Set(selectedProvinceCodes)
            )
                .slice(0, MAX_VISIBLE_TAGS)
                .reduce((count, provinceCount) => count + provinceCount, 0);

            return Math.max(
                0,
                selectedProvinceCodes.length - visibleProvinceCount
            );
        }, [selectedProvinceCodes, treeData]);

        const showAllChip =
            summarizeAll &&
            allProvinceCodes.length > 0 &&
            selectedProvinceCodes.length === allProvinceCodes.length;

        const handleChange = (newValue?: (string | number)[]) => {
            if (!newValue || newValue.length === 0) {
                onChange?.([]);
                return;
            }

            const hasSelectAll = newValue.includes(SELECT_ALL_VALUE);
            const hadSelectAll =
                allProvinceCodes.length > 0 &&
                selectedProvinceCodes.length === allProvinceCodes.length;

            if (hasSelectAll && !hadSelectAll) {
                onChange?.([...allProvinceCodes]);
                return;
            }

            const expandedProvinceCodes = new Set<string>();
            newValue
                .filter((val) => val !== SELECT_ALL_VALUE)
                .forEach((val) => {
                    if (typeof val === 'string') {
                        if (val.startsWith('bu-')) {
                            const buId = parseInt(val.replace('bu-', ''), 10);
                            const provinceCodes = businessUnitToProvincesMap.get(
                                buId
                            );
                            if (provinceCodes) {
                                provinceCodes.forEach((provinceCode) =>
                                    expandedProvinceCodes.add(provinceCode)
                                );
                            }
                        } else if (val.startsWith('province-')) {
                            const provinceCode = val.replace('province-', '');
                            expandedProvinceCodes.add(provinceCode);
                        }
                    }
                });

            const finalProvinceCodes = Array.from(expandedProvinceCodes);

            if (finalProvinceCodes.length === allProvinceCodes.length) {
                onChange?.([...allProvinceCodes]);
            } else {
                onChange?.(finalProvinceCodes);
            }
        };

        return (
            <TreeSelect
                treeData={treeData}
                placeholder="Select a region"
                value={treeSelectValue}
                onChange={handleChange}
                treeCheckable={true}
                showCheckedStrategy="SHOW_PARENT"
                treeDefaultExpandAll={true}
                loading={businessUnitStore.isFetchingBusinessUnits}
                disabled={
                    disabled ||
                    !businessUnitStore.businessUnits ||
                    businessUnitStore.isFetchingBusinessUnits
                }
                className={`${sharedStyle.custom_input} ${style.compact_input}`}
                popupClassName={sharedStyle.custom_input}
                // With no tags shown, the placeholder chip is the only one
                // left, and it reads "All".
                maxTagCount={showAllChip ? 0 : MAX_VISIBLE_TAGS}
                maxTagPlaceholder={(omittedValues) =>
                    showAllChip
                        ? 'All'
                        : `+${hiddenProvinceCount || omittedValues.length}`
                }
                allowClear={false}
                showArrow={showArrow}
                suffixIcon={suffixIcon}
            />
        );
    }
);
